import { Component, EventEmitter, Input, OnInit, Output, AfterViewInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { SwitcherBoxActivation, SwitcherBoxes, SwitcherBoxType, SwitcherDateType, SwitcherNumberType } from "./utils/number-switcher-common-classes";
import { DEFAULT_DROP_DOWN_DELAY_TIME, DelayService } from "../../service/delay.service";
import { fadeInOutAnimation } from '../../../util/animations/fade-in-out.animation';

@Component({
  selector: 'app-numberswitcher',
  templateUrl: './numberswitcher.component.html',
  styleUrls: ['./numberswitcher.component.scss'],
  providers: [DelayService],
  animations: [fadeInOutAnimation(200, 0)],
})

export class NumberswitcherComponent implements OnInit, AfterViewInit {
  private _destroyStream: Subject<void> = new Subject<void>();
  @Input() switcher = SwitcherBoxType.NUMBER;
  @Output() numberSwitcher = new EventEmitter<SwitcherNumberType>();
  @Output() dateSwitcher = new EventEmitter<SwitcherDateType>();

  boxType = SwitcherBoxType;
  show: boolean = false;

  /**
   * shows the representative active box
   * 1 -> box1
   * 2 -> box2
   * 3 -> box3
   * 4 -> box4 - used only for BoxType.DATE, because there are 4 cases: Day, Month, Quarter, Year
   */
  currentActiveBox = 1;

  boxes = new SwitcherBoxes();
  activeBoxes = new SwitcherBoxActivation(false);


  constructor(private router: Router, private delayService: DelayService) {}

  ngOnInit(): void {
    this.router.events
      .pipe(takeUntil(this._destroyStream))
      .subscribe((val) => {
        if (val instanceof NavigationEnd && !val.urlAfterRedirects.includes('#')) {
          this.activeBoxes = new SwitcherBoxActivation(true);
          this.boxClicked(1);
        }
      });
  }

  ngAfterViewInit(): void {
    this.delayService.initSubscribes(DEFAULT_DROP_DOWN_DELAY_TIME, this.showDropDown, this.hideFundDropDown);
  }

  activateDropDown(): void {
    this.delayService.activate();
  }

  deactivateDropDown(): void {
    this.delayService.deactivate();
  }

  boxClicked(boxNumber: number): void {
    switch (boxNumber) {
      case 1:
        this.currentActiveBox = 1;
        switch (this.switcher) {
          case SwitcherBoxType.NUMBER:
            this.switchToNumber(SwitcherNumberType.REGULAR);
            break;
          case SwitcherBoxType.DATE:
            this.switchToDate(SwitcherDateType.DAY);
            break;
        }
        break;
      case 2:
        this.currentActiveBox = 2;
        switch (this.switcher) {
          case SwitcherBoxType.NUMBER:
            this.switchToNumber(SwitcherNumberType.THOUSAND);
            break;
          case SwitcherBoxType.DATE:
            this.switchToDate(SwitcherDateType.MONTH);
            break;
        }
        break;
      case 3:
        this.currentActiveBox = 3;
        switch (this.switcher) {
          case SwitcherBoxType.NUMBER:
            this.switchToNumber(SwitcherNumberType.MILLION);
            break;
          case SwitcherBoxType.DATE:
            this.switchToDate(SwitcherDateType.QUARTER);
            break;
        }
        break;
      case 4:
        this.currentActiveBox = 4;
        this.switchToDate(SwitcherDateType.YEAR);
        break;
    }
  }

  switchToDate(type: SwitcherDateType): void {
    this.dateSwitcher.emit(type);
    this.setBoxActive(this.currentActiveBox);
  }

  switchToNumber(type: SwitcherNumberType): void {
    this.numberSwitcher.emit(type);
    this.setBoxActive(this.currentActiveBox);
  }

  setBoxActive(boxNumber: number): void {
    // Reset all boxes to false initially
    this.activeBoxes = new SwitcherBoxActivation(false);

    // Set the specified box to true based on boxNumber
    switch (boxNumber) {
      case 1:
        this.activeBoxes.box1 = true;
        break;
      case 2:
        this.activeBoxes.box2 = true;
        break;
      case 3:
        this.activeBoxes.box3 = true;
        break;
      case 4:
        this.activeBoxes.box4 = true;
        break;
      default:
        this.activeBoxes.box1 = true;
        break;
    }
  }

  private showDropDown: () => void = () => {
    this.show = true;
  };

  private hideFundDropDown: () => void = () => {
    this.show = false;
  };
}
